import { useEffect, useState } from 'react'
import { Icon } from '@iconify/react'
import { ArrayParam, NumberParam, useQueryParam } from 'use-query-params'
import {
  Button,
  Dropdown,
  Checkbox,
  useGetTeamMembers,
  Input,
} from '@pepper/ui'

import { useSelectedCompanyId } from 'utils/functions/misc'
import s from './WorkflowAssigneeFilter.module.css'

export const WorkflowAssigneeFilter = () => {
  const [open, setOpen] = useState(false)
  const [assigneeIds, setAssigneeIds] = useQueryParam('assignee', ArrayParam)
  const [_, setCurrentPage] = useQueryParam('currentPage', NumberParam)
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useState<
    (string | null)[]
  >([])
  const companyId = useSelectedCompanyId()
  const { data: assignees } = useGetTeamMembers({ companyId })
  const [matchingAssignees, setMatchingAssignees] = useState<
    TeamMembers[] | undefined
  >([])
  const [inputText, setInputText] = useState('')

  const isChecked = (checkValue: string) => {
    return selectedAssigneeIds?.includes(checkValue)
  }

  const handleChange = (statusId: string) => {
    if (isChecked(statusId)) {
      setSelectedAssigneeIds(selectedAssigneeIds.filter(id => id !== statusId))
    } else {
      setSelectedAssigneeIds([...selectedAssigneeIds, statusId])
    }
  }

  const handleApply = () => {
    setAssigneeIds(selectedAssigneeIds)
    setCurrentPage(0)
    setOpen(false)
  }

  const onVisibleChange = (visible: boolean) => {
    assigneeIds && setSelectedAssigneeIds(assigneeIds)
    setOpen(visible)
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(event.target.value)
    setMatchingAssignees(
      assignees?.filter(assignee =>
        assignee.full_name
          .toLowerCase()
          .includes(event.target.value.toLowerCase())
      )
    )
  }

  const handleClose = () => {
    setInputText('')
    setMatchingAssignees(assignees)
    setAssigneeIds(undefined)
    setSelectedAssigneeIds([])
    setCurrentPage(0)
    setOpen(false)
  }

  const normalizedAssignees: Record<string, TeamMembers> | undefined =
    assignees?.reduce(
      (prev, curr) => ({
        ...prev,
        [curr.id]: curr,
      }),
      {}
    )

  const appliedFilterText = (): string => {
    if (assigneeIds?.length === 0 || !assigneeIds) {
      return ''
    } else if (assigneeIds?.length === 1 && assigneeIds[0] !== null) {
      return normalizedAssignees?.[assigneeIds?.[0]].full_name ?? ''
    } else {
      return `${assigneeIds?.length} selected`
    }
  }

  useEffect(() => {
    setMatchingAssignees(assignees)
  }, [assignees])

  useEffect(() => {
    setSelectedAssigneeIds(assigneeIds ?? [])
  }, [assigneeIds])

  const menu = () => {
    return (
      <div className={s.wrapper}>
        <Input
          onChange={handleInputChange}
          prefix={<Icon icon="lucide:search" className={s.searchIcon} />}
          placeholder="Search assignee"
          className={s.input}
          value={inputText}
        />
        <div className={s.options}>
          {matchingAssignees?.map(assignee => (
            <Checkbox
              key={assignee.id}
              onChange={() => handleChange(assignee.id)}
              checked={isChecked(assignee.id)}
              name={assignee.id}
              className={s.option}
            >
              <div className={s.assigneeName}>{assignee.full_name}</div>
            </Checkbox>
          ))}
        </div>
        <div className={s.divider} />
        <div className={s.actions}>
          <Button className={s.actionBtn} type="primary" onClick={handleApply}>
            Apply
          </Button>
          <Button className={s.actionBtn} type="ghost" onClick={handleClose}>
            Reset
          </Button>
        </div>
      </div>
    )
  }

  return (
    <Dropdown
      overlay={menu}
      trigger={['click']}
      visible={open}
      onVisibleChange={onVisibleChange}
    >
      <Button size="small" className={s.button}>
        Assignee
        <div className={s.selected}>{appliedFilterText()}</div>
        <Icon
          icon="material-symbols:keyboard-arrow-down-rounded"
          width={16}
          height={16}
        />
      </Button>
    </Dropdown>
  )
}
